<template>
  <lb-dialog
    :value="visible"
    :heading="dialogHeading"
    content-class="bulk-preparer-editor-dialog"
    :loading="saving"
    :data-testid="`${testIdPrefix}__bulk-edit-preparer-dialog`"
    @input="$emit('update:visible', $event)"
  >
    <template v-slot:body>
      <div class="bulk-preparer-editor-dialog__body">
        <UserChipMultiSelect
          :value="localPreparerIds"
          :users="locationScopedUsers"
          :loading="usersFetchLoading"
          variant="assignment-dialog"
          :label="roleLabels.BULK_SEARCH_PREPARER"
          :current-users-label="roleLabels.BULK_CURRENT_PREPARERS"
          role-label="preparer"
          placeholder="Find by name or email..."
          :data-test-id="`${testIdPrefix}__bulk-edit-preparer-users`"
          @input="onPreparerIdsChange"
        />

        <p v-if="errorMessage" class="bulk-preparer-editor-dialog__error">{{ errorMessage }}</p>
      </div>
    </template>

    <template v-slot:actions>
      <div class="bulk-preparer-editor-dialog__actions">
        <base-button
          label="Cancel"
          secondary
          :disabled="saving"
          :data-testid="`${testIdPrefix}__bulk-edit-preparer-cancel`"
          @click="close"
        />
        <base-button
          label="Save"
          primary
          :loading="saving"
          :disabled="saving"
          :data-testid="`${testIdPrefix}__bulk-edit-preparer-save`"
          @click="save"
        />
      </div>
    </template>
  </lb-dialog>
</template>

<script>
import './BulkPreparerEditorDialog.scss';
import appConstants from '@/plugins/constants';
import { USER_ROLE_LABEL } from '../../utils/constants';
import UserChipMultiSelect from '../UserChipMultiSelect/UserChipMultiSelect.vue';
import { normalizeUserIdList } from '../../views/Setup/TaskList/utils/assignmentRoles';
import { areSameIdLists, buildBulkRoleTargetIds } from '../../utils/bulkApproverEditorUtils';
import {
  fetchUsersForLocations,
  filterUsersToAllowedIds,
  dropIdsNotAllowed,
} from '../../utils/locationUserAccess';

export default {
  name: 'BulkPreparerEditorDialog',

  components: { UserChipMultiSelect },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    users: {
      type: Array,
      default: () => [],
    },
    preparerIdsFromTasks: {
      type: Array,
      default: () => [],
    },
    approverIdsFromTasks: {
      type: Array,
      default: () => [],
    },
    signOffIdsFromTasks: {
      type: Array,
      default: () => [],
    },
    initialPreparerIds: {
      type: Array,
      default: () => [],
    },
    testIdPrefix: {
      type: String,
      default: 'tasklist',
    },
    locationIds: {
      type: Array,
      default: () => [],
    },
    cycleId: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      localPreparerIds: [],
      preparerDirty: false,
      baselinePreparerIds: [],
      errorMessage: '',
      allowedUserIds: [],
      usersFetchLoading: false,
      usersFetchToken: 0,
    };
  },

  computed: {
    roleLabels() {
      return USER_ROLE_LABEL;
    },

    dialogHeading() {
      return `Edit ${USER_ROLE_LABEL.PREPARER}`;
    },

    blockedApproverIdSet() {
      return new Set((this.approverIdsFromTasks || []).map(String));
    },

    blockedSignOffIdSet() {
      return new Set((this.signOffIdsFromTasks || []).map(String));
    },

    assignableUsers() {
      return (this.users || []).filter(
        (user) => user?._id && this.isAssignablePreparerId(user._id),
      );
    },

    locationScopedUsers() {
      return filterUsersToAllowedIds(this.assignableUsers, this.allowedUserIds);
    },
  },

  watch: {
    // Immediate: the parent drawer mounts lazily, already open.
    visible: {
      handler(isOpen) {
        if (isOpen) {
          this.reset();
          this.fetchAllowedUsers();
        }
      },
      immediate: true,
    },
    initialPreparerIds: {
      handler(newIds) {
        if (!this.visible) return;
        const incoming = normalizeUserIdList(newIds)
          .map(String)
          .filter((id) => this.isAssignablePreparerId(id));
        if (areSameIdLists(incoming, this.baselinePreparerIds)) return;
        this.reset();
      },
      deep: true,
    },
  },

  methods: {
    isAssignablePreparerId(id) {
      const userId = String(id);
      return !this.blockedApproverIdSet.has(userId) && !this.blockedSignOffIdSet.has(userId);
    },

    async fetchAllowedUsers() {
      const locationIds = [...new Set((this.locationIds || []).map(String).filter(Boolean))];
      if (!locationIds.length) {
        this.usersFetchToken += 1;
        this.usersFetchLoading = false;
        this.allowedUserIds = [];
        this.localPreparerIds = dropIdsNotAllowed(this.localPreparerIds, this.allowedUserIds);
        return;
      }
      const token = ++this.usersFetchToken;
      this.usersFetchLoading = true;
      try {
        const ids = await fetchUsersForLocations({
          locationIds,
          cycleId: this.cycleId,
        });
        if (token !== this.usersFetchToken) return;
        this.allowedUserIds = ids;
        this.localPreparerIds = dropIdsNotAllowed(this.localPreparerIds, ids);
      } catch (err) {
        if (token !== this.usersFetchToken) return;
        console.error('[BulkPreparerEditorDialog]: usersForLocations error', err);
      } finally {
        if (token === this.usersFetchToken) this.usersFetchLoading = false;
      }
    },

    onPreparerIdsChange(ids) {
      const allowed = normalizeUserIdList(ids).filter((id) => this.isAssignablePreparerId(id));
      this.localPreparerIds = allowed;
      this.preparerDirty = true;
      this.errorMessage = '';
    },

    reset() {
      this.baselinePreparerIds = [...(this.initialPreparerIds || [])]
        .map(String)
        .filter((id) => this.isAssignablePreparerId(id));
      this.localPreparerIds = [...this.baselinePreparerIds];
      this.preparerDirty = false;
      this.errorMessage = '';
    },

    close() {
      this.$emit('update:visible', false);
    },

    validate(preparerIds) {
      const overlapMessage = appConstants.TASK_LIST_CONSTANTS.ERROR_MESSAGES.PREPARER_APPROVER_OVERLAP;
      const overlap = preparerIds.filter(
        (id) => this.blockedApproverIdSet.has(String(id)) || this.blockedSignOffIdSet.has(String(id)),
      );
      if (overlap.length) {
        this.errorMessage = overlapMessage;
        return false;
      }
      this.errorMessage = '';
      return true;
    },

    buildSavePayload() {
      let preparerIds = buildBulkRoleTargetIds({
        dirty: this.preparerDirty,
        localIds: this.localPreparerIds,
        unionIds: this.preparerIdsFromTasks,
        intersectionBaselineIds: this.baselinePreparerIds,
      }).filter((id) => this.isAssignablePreparerId(id));

      if (this.preparerDirty) {
        const merged = new Set(preparerIds.map(String));
        this.localPreparerIds.forEach((id) => {
          if (this.isAssignablePreparerId(id)) merged.add(String(id));
        });
        preparerIds = [...merged];
      }

      return { preparerIds };
    },

    save() {
      if (!this.preparerDirty) {
        this.close();
        return;
      }
      const { preparerIds } = this.buildSavePayload();
      if (!this.validate(preparerIds)) return;
      this.$emit('save', { preparerIds });
    },
  },
};
</script>
